import React, { useEffect, useRef, useState } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import cc from "classcat"
import { Link } from "gatsby"
import useTranslations from "hooks/useTranslations"
import { Container } from "components/atoms/materials/Container"
import PlayButton from "components/atoms/PlayButton"
import Modal from "components/molecules/Modal"
import ContactButton from "components/molecules/ContactButton"
import YouTubeThumbnail from "components/atoms/YouTubeThumbnail"
import Button from "components/buttons/Button"

function formatYouTubeDuration(duration) {
  const hoursMatch = duration.match(/(\d+)H/)
  const minutesMatch = duration.match(/(\d+)M/)
  const secondsMatch = duration.match(/(\d+)S/)

  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0
  const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0

  const formattedHours = hours.toString().padStart(2, "0")
  const formattedMinutes = minutes.toString().padStart(2, "0")
  const formattedSeconds = seconds.toString().padStart(2, "0")

  return hours > 0
    ? `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`
}

function LectureLink({ text, link }) {
  return (
    <a
      className="inline-flex "
      target="_blank"
      rel="noopener noreferrer"
      href={link}
    >
      <span className="text-sm font-bold ">{text}</span>
    </a>
  )
}

const Description = ({ children }) => (
  <p className="mb-0 mt-2 space-y-1 whitespace-pre-line text-sm text-slate-600">
    {children}
  </p>
)

interface LecturesProps {
  lectures: Array<Lecture>
  sectionId?: string
  showContactButton?: boolean
}

export function Lectures({
  lectures,
  sectionId,
  showContactButton,
}: LecturesProps) {
  const { publishSoon, videoDuration, buttonLogin } = useTranslations()
  const appLink = process.env.GATSBY_TOOSA_URL

  const [show, setShow] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [videoDurations, setVideoDurations] = useState({})

  const filteredLectures = lectures.filter(
    (lecture) => lecture.link || lecture.videoLink || lecture.description,
  )

  const openModalWithVideo = (videoLink) => {
    setSelectedVideo(videoLink)
    setShow(true)

    // Update URL with the video ID hash without page reload
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.hash = `video-${videoLink}`
      window.history.pushState({}, "", url.toString())
    }
  }

  const closeModal = () => {
    setSelectedVideo(null)
    setShow(false)

    // Remove the hash from URL without page reload
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.hash = ""
      window.history.pushState({}, "", url.toString())
    }
  }

  useEffect(() => {
    const handleHashChange = () => {
      if (typeof window !== "undefined") {
        const { hash } = window.location

        if (hash && hash.startsWith("#video-")) {
          const videoId = hash.replace("#video-", "")

          // Find if this video exists in our lectures
          const lectureWithVideo = filteredLectures.find(
            (lecture) => lecture.videoLink === videoId,
          )

          if (lectureWithVideo) {
            setSelectedVideo(videoId)
            setShow(true)
          }
        }
      }
    }

    handleHashChange()

    window.addEventListener("hashchange", handleHashChange)

    return () => {
      window.removeEventListener("hashchange", handleHashChange)
    }
  }, [filteredLectures])

  // Create a function to generate a URL-friendly ID for each lesson
  const getLessonId = (title, index) => {
    // Create a slug from the title (simple version)
    const slug = title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .slice(0, 50) // Limit length

    return `lesson-${index + 1}-${slug}`
  }

  useEffect(() => {
    const fetchVideoDuration = async (id) => {
      if (id) {
        try {
          const response = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${id}&key=AIzaSyB4f2-UFx7v9UFlVA-8_XHx_C25whP9E_4`,
          )
          const data = await response.json()
          const duration = data?.items?.[0]?.contentDetails?.duration

          if (duration) {
            const formattedDuration = formatYouTubeDuration(duration)

            setVideoDurations((prevDurations) => ({
              ...prevDurations,
              [id]: formattedDuration,
            }))
          }
        } catch (error) {
          console.error("Error fetching video duration:", error)
        }
      }
    }

    lectures.forEach((lecture) => {
      if (lecture.videoLink && !videoDurations[lecture.videoLink]) {
        fetchVideoDuration(lecture.videoLink)
      }
    })
  }, [lectures, videoDurations])

  return (
    <Container size="lg" className="mt-16">
      <ol
        className={cc([
          "mb-0 ml-0 grid grid-cols-1 gap-x-8 gap-y-10 [counter-reset:video] sm:grid-cols-2",
          { "lg:grid-cols-3": filteredLectures.length <= 3 },
          { "lg:grid-cols-4": filteredLectures.length === 4 },
          { "lg:grid-cols-3": filteredLectures.length >= 5 },
        ])}
      >
        {filteredLectures.map(
          (
            { title, videoLink, description, linkText, link, coverImage, free },
            index,
          ) => {
            const lessonId = getLessonId(title, index)
            const durationText =
              videoLink && videoDurations[videoLink]
                ? `${videoDuration} ${videoDurations[videoLink]}.`
                : ""
            const gatsbyImage = getImage(coverImage)

            return (
              <li
                key={title}
                id={lessonId}
                className="prose scroll-mt-24 space-y-4 [counter-increment:lecture]"
              >
                <div className="">
                  <div
                    className={cc(["relative", { "cursor-pointer": free }])}
                    onClick={() => {
                      if (!free) {
                        // For non-free lessons, let the Button component handle the navigation
                        return
                      }
                      if (gatsbyImage) return null
                      openModalWithVideo(videoLink)
                    }}
                  >
                    <div
                      className={cc([
                        "relative flex aspect-video overflow-hidden rounded-lg shadow-lg",
                        {
                          "place-content-center bg-gradient-to-b from-slate-600 to-slate-700 bg-cover":
                            !videoLink,
                        },
                        { "opacity-50": !free },
                      ])}
                    >
                      {videoLink && !gatsbyImage ? (
                        <>
                          <YouTubeThumbnail
                            videoId={videoLink}
                            alt="Video thumbnail"
                            className="isolate m-0 w-full overflow-hidden object-cover"
                            loading="lazy"
                          />
                          <PlayButton />
                        </>
                      ) : null}
                      {gatsbyImage && (
                        <GatsbyImage
                          image={gatsbyImage}
                          alt={title}
                          className="isolate m-0 w-full overflow-hidden object-cover"
                        />
                      )}
                      {!videoLink && !gatsbyImage ? (
                        <div className="absolute top-1/2">
                          <div className="relative -translate-y-1/2 text-center font-display text-3xl font-bold text-slate-300">
                            {publishSoon}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="absolute inset-0  flex items-center justify-center">
                      {!free &&
                        (showContactButton ? (
                          <ContactButton bgStyle="primary" />
                        ) : (
                          <Button
                            bgStyle="primary"
                            label={buttonLogin}
                            link={`${appLink}/lessons/${sectionId}`}
                            outbound
                            target="_blank"
                          />
                        ))}
                    </div>
                  </div>

                  <h3 className="mb-0 mt-8 text-base font-medium tracking-tight text-slate-900 before:mb-2 before:block before:font-mono before:text-sm before:text-slate-500 before:content-[counter(lecture,decimal-leading-zero)]">
                    <a
                      href={`#${lessonId}`}
                      className="mr-2 inline-flex items-center text-slate-400 hover:text-slate-700"
                      title="Direct link to this lesson"
                      aria-label="Direct link to this lesson"
                      onClick={(e) => {
                        e.preventDefault()
                        if (typeof window !== "undefined") {
                          const url = new URL(window.location.href)
                          const sectionPart = sectionId ? `#${sectionId}` : ""
                          url.hash = `${sectionPart}#${lessonId}`
                          window.history.pushState({}, "", url.toString())
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                    </a>
                    {title}
                  </h3>
                  {Array.isArray(description) ? (
                    description.map((line, index) => (
                      <Description key={index}>
                        {line}
                        {index === description.length - 1
                          ? ` ${durationText}`
                          : ""}
                      </Description>
                    ))
                  ) : (
                    <>
                      <Description>{description}</Description>
                      <Description>{durationText}</Description>
                    </>
                  )}
                </div>
                {link && linkText && free ? (
                  <LectureLink text={linkText} link={link} />
                ) : null}
              </li>
            )
          },
        )}
      </ol>
      <Modal
        isOpen={show}
        onRequestClose={closeModal}
        showCloseButton={false}
        closeOnClickAway
      >
        <iframe
          className="mb-0 aspect-video rounded-lg sm:w-[600px]  md:w-[700px]"
          src={`https://www.youtube-nocookie.com//embed/${selectedVideo}?autoplay=1&rel=0&cc_load_policy=1&hl=en`}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Modal>
    </Container>
  )
}
